<template>
  <PageHeader
    :title="t('navbar.events')"
    summary=""
  >
    <template #actions>
      <SpmButton
        v-if="findPermission('my_events.create')"
        :label="t('events.create')"
        icon="far fa-plus-circle"
        class-style="p-button p-button-success"
        @click="showEditorModal"
      />
    </template>
  </PageHeader>

  <div class="grid">
    <div class="col-12 col-fixed customers">
      <SpmTableApi
        ref="spmTableApiRef"
        type-api="client_api"
        route="/events"
        name="Events"
        index="id_event"
        :persistent-filters="persistentFilters"
        :table-columns="columns"
        :id-shop="idShop"
        grouped-actions-key="id_event"
      />
    </div>
  </div>
  <ConfirmPopup group="basicGroup" />
  <EventsEditorModal
    v-if="showEventEditorModal"
    :id-event="idEvent"
    :event-definition="customEventDefinition"
    @close="showEditorModal"
  />
</template>

<script lang="ts">
import {
  defineComponent, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import PageHeader from '@/components/layout/PageHeader.vue';
import SpmTableApi from '@/components/table/SpmTableApi.vue';
import EventsEditorModal from '@/components/events/EventsEditorModal.vue';
import {
  eventsTypeEnum, SpmTableAction,
  SpmTableColumns, SpmTableFilter,
} from '@/types';
import { OperatorType } from '@/types/generated-types/graphql';
import { findPermission, UserState } from '@/composables/User';
import Tooltip from 'primevue/tooltip';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import ConfirmPopup from 'primevue/confirmpopup';
import { useConfirm } from 'primevue/useconfirm';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { showToastError, showToastSuccess } from '@/helpers';
import { Event } from '@/types/events-types';

export default defineComponent({
  name: 'Events',
  components: {
    PageHeader,
    SpmTableApi,
    EventsEditorModal,
    SpmButton,
    ConfirmPopup,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const spmTableApiRef = ref();
    const idShop = UserState.activeShop?.id ?? 0;
    const confirm = useConfirm();

    const idEvent = ref<number | null>(null);
    const customEventDefinition = ref<any>(null);

    const showEventEditorModal = ref(false);
    const toggleEditorModal = () => {
      showEventEditorModal.value = !showEventEditorModal.value;
    };

    const editEvent = (event: Event) => {
      idEvent.value = event.id_event ?? null;
      showEventEditorModal.value = true;
    };
    const duplicateEvent = async (event: Event): Promise<Record<string, any>> => {
      const newName = t('events.confirm.duplicate.cloneText') + event.name;
      const newCodeName = `${event.name}_copy`;
      const newEvent = {
        name: newName,
        code_name: newCodeName,
        type: event.type,
        properties: JSON.parse(event.properties),
      };

      return nestCall('post', NestApiType.CLIENT_API, '/events', {}, '', newEvent);
    };
    const askDuplicateEvent = (event: Event, e: any) => {
      confirm.require({
        group: 'basicGroup',
        target: e.currentTarget,
        message: t('events.confirm.duplicate.message'),
        icon: 'fal fa-info-circle',
        acceptLabel: t('validate'),
        acceptClass: 'p-button-success',
        rejectLabel: t('cancel'),
        rejectClass: 'p-button-secondary',
        accept: () => {
          duplicateEvent(event).then((result: Record<string, any>) => {
            if (result.success) {
              showToastSuccess(t('events.confirm.duplicate.success.message'));
              spmTableApiRef.value.reloadData();
            } else {
              showToastError(t('events.confirm.duplicate.error.message'));
            }
          });
        },
      });
    };
    const deleteEvent = async (event: Event) => nestCall('delete', NestApiType.CLIENT_API, `/events/${event.id_event}`);
    const askDeleteEvent = (event: Event, e: any) => {
      confirm.require({
        group: 'basicGroup',
        target: e.currentTarget,
        message: t('events.confirm.delete.message'),
        icon: 'fal fa-info-circle',
        acceptLabel: t('yes'),
        acceptClass: 'p-button-danger',
        rejectLabel: t('no'),
        rejectClass: 'p-button-secondary',
        accept: () => {
          deleteEvent(event).then((result: Record<string, any>) => {
            if (result.success) {
              showToastSuccess(t('events.confirm.delete.success.message'));
              spmTableApiRef.value.reloadData();
            } else {
              showToastError('GENERIC_ERROR');
            }
          });
        },
      });
    };

    const persistentFilters = ref<SpmTableFilter[]>([
      {
        field: 'type',
        value: eventsTypeEnum.EXTERNAL,
        operator: OperatorType.Equals,
      },
    ]);
    const actions: SpmTableAction[] = [
      {
        icon: 'far fa-edit',
        label: t('events.edit'),
        callback: (event: Event) => editEvent(event),

        show: () => findPermission('my_events.edit'),
      },
      {
        icon: 'far fa-clone',
        label: t('events.duplicate'),
        callback: (event: Event, e: any) => (e && event ? askDuplicateEvent(event, e) : false),
        show: () => findPermission('my_events.duplicate'),
      },
      {
        icon: 'far fa-trash',
        label: t('events.delete'),
        callback: (event: Event, e: any) => (e && event ? askDeleteEvent(event, e) : false),
        show: () => findPermission('my_events.delete'),
      },
    ];
    const columns: SpmTableColumns[] = [
      {
        field: 'id_event',
        header: t('events.headers.id_event'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'name',
        header: t('events.headers.name'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'code_name',
        header: t('events.headers.code_name'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
      },
      {
        field: 'properties',
        header: t('events.headers.properties'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'text',
        hidden: true,
      },
      {
        field: 'date_last_event',
        header: t('events.headers.date_last_event'),
        sortable: true,
        filterable: true,
        editable: false,
        style: '',
        type: 'datetime',
        filterSettings: { type: 'date', hideFilterMenu: true },
      },
      {
        field: '', header: t('events.headers.actions'), sortable: false, filterable: false, editable: false, style: 'width:10%', type: 'action', actions,
      },
    ];

    return {
      t,
      spmTableApiRef,
      idShop,
      columns,
      findPermission,
      persistentFilters,
      showEventEditorModal,
      idEvent,
      customEventDefinition,
      showEditorModal: toggleEditorModal,
    };
  },
});
</script>
